/**
 * Bootstraps the account-scoped SignalR hubs at the app shell. Two connections
 * live as long as the session stays on one account: `/hubs/instances` and
 * `/hubs/dashboard`, both dialed for the account the person is acting for
 * (`AppRouter` re-keys this component on a switch). On every server-pushed
 * event we invalidate the relevant React Query keys so widgets re-fetch
 * through the standard REST path — this keeps the client cache as the single
 * source of truth and avoids hand-merging deltas.
 *
 * Connection lifecycle (start, reconnect, visibility gating, auth circuit
 * breaker) is owned by `manageHubConnection` — this component only wires
 * handlers and resyncs after reconnect gaps.
 */
import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../auth/AuthProvider'
import { useAccount } from '../auth/AccountProvider'
import { manageHubConnection } from '../../api/signalr'
import { reportAccountAccessLost } from '../../api/activeAccount'
import type { ActiveInstance } from '../../api/client'
import { qk } from './queryKeys'
import { pushMarketPoint } from './marketHistory'

type MarketUpdatePayload = {
    instanceId: string
    ticker: string
    kind: string
    bestBidCents: number | null
    bestAskCents: number | null
    at: string
}

export const HubProvider = ({ children }: { children: React.ReactNode }) => {
    const queryClient = useQueryClient()
    const { user } = useAuth()
    const { active } = useAccount()
    const accountId = active.isSelf ? null : active.id

    useEffect(() => {
        if (!user) return

        // A reconnect gap means missed pushes — resync every hub-fed key.
        const resync = () => {
            queryClient.invalidateQueries({ queryKey: qk.instances() })
            queryClient.invalidateQueries({ queryKey: qk.dashboardSummary() })
            queryClient.invalidateQueries({ queryKey: qk.games() })
            queryClient.invalidateQueries({ queryKey: qk.engineAnalyses() })
            queryClient.invalidateQueries({ queryKey: qk.adminEngineMetrics() })
        }

        // The server evicted this delegated connection from the account's
        // groups because the grant was revoked (corporate-user-feature §10).
        const onAccountRevoked = () => {
            if (accountId) reportAccountAccessLost(accountId, 'removed')
        }

        const disposeInstances = manageHubConnection('instances', {
            accountId,
            configure: (instances) => {
                instances.on('accountRevoked', onAccountRevoked)
                instances.on('instanceStateChanged', () => {
                    queryClient.invalidateQueries({ queryKey: qk.instances() })
                    queryClient.invalidateQueries({ queryKey: qk.activeInstances() })
                    queryClient.invalidateQueries({ queryKey: qk.dashboardSummary() })
                    // A fill is what moves an instance's state, and fills are
                    // what the chart's trade marks are made of — refresh the
                    // per-instance seed so a mark appears with the transition
                    // that caused it, not minutes later.
                    queryClient.invalidateQueries({ queryKey: qk.instanceScope() })
                })
                instances.on('decisionPublished', () => {
                    queryClient.invalidateQueries({ queryKey: qk.instances() })
                    queryClient.invalidateQueries({ queryKey: qk.activeInstances() })
                })
                instances.on('marketUpdated', (raw: MarketUpdatePayload) => {
                    // Market updates are high-volume, so this handler NEVER
                    // invalidates queries (an invalidation here refetches the
                    // instance lists on every tick). Two cache-only paths:
                    //   1. Push the bid/ask into the in-memory ring buffer the
                    //      price chart subscribes to (§8.3), keyed by ticker —
                    //      each per-team market keeps its own series.
                    //   2. Patch the active-instances cache in place so the chart's
                    //      live point animates without the whole card remounting —
                    //      see plan §3.2 polish-bar requirement. Only updates for
                    //      the instance's mark ticker may patch `market`; the other
                    //      per-team market quotes the complementary price and
                    //      patching both made the card flip between the two.
                    // Both paths only apply to orderbook updates — fills/cancels
                    // carry null prices and must not blank the card.
                    if (raw?.instanceId && raw.ticker && raw.kind === 'OrderBookUpdated') {
                        pushMarketPoint(raw.instanceId, raw.ticker, {
                            at: raw.at ? Date.parse(raw.at) : Date.now(),
                            bestBidCents: raw.bestBidCents ?? null,
                            bestAskCents: raw.bestAskCents ?? null,
                        })
                        queryClient.setQueryData<ActiveInstance[]>(qk.activeInstances(), (prev) => {
                            if (!prev) return prev
                            let changed = false
                            const next = prev.map((a) => {
                                if (a.instanceId !== raw.instanceId || a.markTicker !== raw.ticker)
                                    return a
                                changed = true
                                return {
                                    ...a,
                                    market: {
                                        bestBidCents: raw.bestBidCents,
                                        bestAskCents: raw.bestAskCents,
                                        capturedAt: raw.at ?? new Date().toISOString(),
                                    },
                                }
                            })
                            return changed ? next : prev
                        })
                    }
                })
            },
            onReconnected: resync,
        })

        const disposeDashboard = manageHubConnection('dashboard', {
            accountId,
            configure: (dashboard) => {
                dashboard.on('accountRevoked', onAccountRevoked)
                dashboard.on('summaryChanged', () => {
                    queryClient.invalidateQueries({ queryKey: qk.dashboardSummary() })
                })
                // Server-side value change on a games row (phase / score / schedule),
                // pushed by GameStateProjector (~3s watcher path) and the scoreboard
                // sweep. One invalidation, one bounded board refetch — every games
                // consumer shares the qk.games() cache entry (see useGames).
                dashboard.on('gamesChanged', () => {
                    queryClient.invalidateQueries({ queryKey: qk.games() })
                })
                // New engine shadow analysis persisted (a couple per game
                // night) — invalidate-not-merge is right at this volume; the
                // prefix also refreshes any open detail view. The Engine
                // section's metrics ride the same push (engine-metrics plan
                // §6.3) via their own prefix — deliberately NOT the board
                // layout key, which must stay quiet under data pushes.
                dashboard.on('engineAnalysisPublished', () => {
                    queryClient.invalidateQueries({ queryKey: qk.engineAnalyses() })
                    queryClient.invalidateQueries({ queryKey: qk.adminEngineMetrics() })
                })
            },
            onReconnected: resync,
        })

        return () => {
            disposeInstances()
            disposeDashboard()
        }
    }, [user, queryClient, accountId])

    return <>{children}</>
}
